import { z } from 'zod';

import { isSafeName, SAFE_NAME_MESSAGE } from '../../validators/safe-name';
import { RetryPolicySchema } from '../../rules/schemas/connection-rules.schema';
import {
  FLOW_GRAPH_SCHEMA_VERSION,
  MAIN_PORT,
  NODE_KEY_PATTERN,
  PORT_KEY_PATTERN,
} from '../constants/flow.constants';

export const NodeKeySchema = z
  .string()
  .regex(NODE_KEY_PATTERN, 'a node key is lowercase letters, digits and _, up to 40');

export const PortKeySchema = z
  .string()
  .regex(PORT_KEY_PATTERN, 'a port is lowercase letters, digits and _, up to 32');

// Explicit numbers, no connection-sized defaults: a node that states a retry says all of it.
export const FlowNodeRetrySchema = z.object({
  strategy: z.enum(['exponential', 'linear', 'fixed']).default('exponential'),
  maxAttempts: z.number().int().min(1).max(200),
  initialSec: z.number().int().positive().max(86_400),
  multiplier: z.number().positive().max(100).default(2),
  capSec: z.number().int().positive().max(86_400),
  jitterPct: z.number().int().min(0).max(100).default(10),
  retryOn: RetryPolicySchema.shape.retryOn.optional(),
  onAmbiguous: z.enum(['retry', 'fail']).default('retry'),
});

export const FlowNodeSchema = z.object({
  key: NodeKeySchema,
  // A string, not an enum: an unknown type is a validator finding pinned to its node.
  type: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80).refine(isSafeName, SAFE_NAME_MESSAGE),
  config: z.record(z.string(), z.unknown()).default({}),
  position: z.tuple([z.number(), z.number()]).optional(),
  disabled: z.boolean().optional(),
  retry: FlowNodeRetrySchema.optional(),
  children: z.array(NodeKeySchema).max(200).optional(),
});

export const FlowEdgeSchema = z.object({
  from: NodeKeySchema,
  output: PortKeySchema,
  to: NodeKeySchema,
  input: PortKeySchema.default(MAIN_PORT),
  handled: z.boolean().optional(),
});

export const FlowGraphSchema = z.object({
  schema: z.literal(FLOW_GRAPH_SCHEMA_VERSION),
  nodes: z.array(FlowNodeSchema).max(2_000),
  edges: z.array(FlowEdgeSchema).max(4_000),
});

export type TFlowNodeRetry = z.output<typeof FlowNodeRetrySchema>;
export type TFlowNode = z.output<typeof FlowNodeSchema>;
export type TFlowEdge = z.output<typeof FlowEdgeSchema>;
export type TFlowGraph = z.output<typeof FlowGraphSchema>;
export type TFlowGraphInput = z.input<typeof FlowGraphSchema>;

export function emptyFlowGraph(): TFlowGraph {
  return { schema: FLOW_GRAPH_SCHEMA_VERSION, nodes: [], edges: [] };
}
